import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../../i18n";
import router from "../../../../../router";
import store from "../../../../../store";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../../utils/button";
import { modalStyleForm, modalStyleIFrame } from "../../../../../utils/constant";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickDelete,
    onClickDeleteAll
} from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { has } from "../../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../../utils/page";
import { json, required, validateError } from "../../../../../utils/validate";
import { createConditionAction, createConditionForm } from "./lookup.condition";
import { createColumns, createColumnsForIconList, createField } from "./lookup.section";

const CIFrame = defineAsyncComponent(() => import("../../../../../components/iframe/CIFrame"));
const LookupForm = defineAsyncComponent(() => import("./LookupForm"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/lookup";
export const routerPrefix = "lookups";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ page, state, group }) => {
    const sort = page.sort || [];
    const where = page.where || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({
        criteria: {
            ...page,
            data: [],
            where: [...where, { name: "group", type: "String", operator: "=", value: group }],
            sort: [...sort, { name: "refParent.key", type: "ASC" }, { name: "seq", type: "ASC" }]
        }
    })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, state, group, formParams }) => {
    const { viewOnly } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs, state, group });
                    else onClickView({ record, routerPrefix, group });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickCreate = ({ routerPrefix, group }) => {
    router.push({ name: `${routerPrefix}-create`, params: { group } });
};

export const onClickView = ({ routerPrefix, record, group }) => {
    router.push({ name: `${routerPrefix}-view`, params: { id: record.id, group } });
};

export const onClickEdit = ({ routerPrefix, record, group }) => {
    router.push({ name: `${routerPrefix}-edit`, params: { id: record.id, group } });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [{ name: "children", select: ["name"], page: 0, size: 1000000, isRecursive: true }]
        };
    }
};

export const createForm = ({ group }) => {
    return {
        refParent: null,
        group,
        key: null,
        value: null,
        seq: null,
        attribute: null,
        attributeStr: null,
        children: []
    };
};

export const createRules = () => {
    return {
        key: [required],
        value: [required],
        seq: [required],
        attributeStr: [json]
    };
};

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.key : i18n.global.t("button.create");
};

export const createGroupTitle = ({ group, value }) => {
    if (group === "ICON") {
        return null;
    }
    // if (i18n.global.te(`views.lookup.${group}`)) {
    //     return i18n.global.t(`views.lookup.${group}`) + " (" + group + ")";
    // }
    if (value && value !== group) {
        return value + " (" + group + ")";
    }
    return group;
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise
        .then(page => {
            initUseForm({ data, page, routerPrefix }); // init for check data from server
            modelRef.attributeStr = page.data[0]?.attribute ? JSON.stringify(page.data[0]?.attribute, null, 4) : "";
            initUseForm({ data, page: { data: [modelRef] }, routerPrefix }); // init for initialModel
        })
        .finally(() => setIsLoading(false));
};

export const initState = ({ props, group, forIcon }) => {
    const { menuContext } = props;
    if (stateGroup[group] && !forIcon) {
        return stateGroup[group];
    }
    const state = {
        group,
        columns: forIcon ? createColumnsForIconList() : createColumns({ group }),
        page: createPage({ options: { select: ["*", "refParent.*"] } }),
        tableOptions: { search: { activeKey: "Search" } },
        conditionAction: createConditionAction()
    };
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => createGroupTitle({ group: d.key })).join(", ") }),
            onClick: ({ rowSelection }) =>
                onClickActiveAll({
                    rowSelection,
                    callActiveAll,
                    state,
                    onSearch: search => onSearch({ props, state, group, ...search }),
                    setIsLoading
                })
        }),
        createManageArchive({
            menuContext,
            confirm: ({ rowSelection }) => ({
                content: rowSelection.selectedRows.map(d => createGroupTitle({ group: d.key })).join(", "),
                okType: "danger"
            }),
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({
                    rowSelection,
                    callArchiveAll,
                    state,
                    onSearch: search => onSearch({ props, state, group, ...search }),
                    setIsLoading
                })
        }),
        createManageDelete({
            menuContext,
            confirm: ({ rowSelection }) => ({
                content: rowSelection.selectedRows.map(d => createGroupTitle({ group: d.key })).join(", "),
                okType: "danger"
            }),
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({
                    rowSelection,
                    callDeleteAll,
                    state,
                    onSearch: search => onSearch({ props, state, group, ...search }),
                    setIsLoading
                })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix, group }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix, group }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix, group }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
    ];
    return (stateGroup[group] = state);
};

export const initForm = ({ props, data, refs, state, group }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs, state, group });
    data.rules = createRules({ props, data, refs, state, group });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, state, group, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, state, group, isLoading });
    if (data.id) {
        data.children = {
            fetchCriteria: {
                select: ["*", "refParent.*"],
                page: 0,
                size: 1000000,
                where: [{ name: "refParent.id", type: "UUID", operator: "=", value: data.id }]
            }
        };
    }
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () =>
                        createAppPageHeaderTop({
                            props,
                            data,
                            refs,
                            state,
                            routerPrefix,
                            onClickView: ({ record, routerPrefix }) => onClickView({ record, routerPrefix, group }),
                            onClickEdit: ({ record, routerPrefix }) => onClickEdit({ record, routerPrefix, group })
                        }),
                    default: () => createAppPageHeaderDefault({ props, data, refs, group, state })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs, state, group }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix, group }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix, group }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix, group }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs, state, group }), classStyle: "m-1" }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix, group })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix, group })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () =>
                    onClickDelete({
                        record: { id },
                        callDelete,
                        setIsLoading: setIsLoadingApp,
                        state,
                        routerPrefix: "lookups-group",
                        routerParams: { group }
                    }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => (
            <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/data-mgt/lookups?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        )
    });
    return options;
};

export const openModalForm = ({ props, items }) => {
    const refs = reactive({ form: null });
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            okButtonProps: { disabled: props.viewOnly },
            header: i18n.global.t("model.lookup.children"),
            onOk: () => {
                refs.form.callOnClickModalOk().then(record => {
                    if (items) {
                        items.push(record);
                    } else {
                        lodash.merge(props.form, record);
                    }
                    options.bind.onCancel();
                });
            }
        }),
        render: () => (
            <LookupForm
                ref={e => {
                    if (e) refs.form = e;
                }}
                {...props}
            ></LookupForm>
        )
    });
    return options;
};

export const stateGroup = [];
